import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
	CFormSelect, CBadge,
} from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { cilPlus, cilSearch, cilPencil } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { useCategoryScopes, useInactiveCategoryScopes } from 'src/hooks/useCategoryScopes';

const urlApi = '/support-categories';
const urlRouter = '/support-categories';
const itemsPerPage = 30;

export async function LoaderSupportCategories() {
	return AxiosInstance.get(urlApi, { params: { page: 1, itemsPerPage } });
}

const SupportCategories = () => {
	const loaderData = useLoaderData();
	const navigate = useNavigate();
	const scopeOptions = useCategoryScopes();
	const inactiveScopeOptions = useInactiveCategoryScopes();
	const [rows, setRows] = useState(loaderData?.data?.data ?? []);
	const [rowCountState, setRowCountState] = useState(loaderData?.data?.total ?? 0);
	const [scopeFilter, setScopeFilter] = useState('');
	const [showLoader, setShowLoader] = useState(false);
	const [page, setPage] = useState(0);
	const isFirstRender = useRef(true);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	const getRowsTable = useCallback((p = 1) => {
		setShowLoader(true);
		const params = { page: p, itemsPerPage };
		if (scopeFilter) {
			params['scope[]'] = scopeFilter;
		}
		AxiosInstance.get(urlApi, { params }).then((response) => {
			setRows(response.data.data ?? []);
			setRowCountState(response.data.total ?? 0);
			setShowLoader(false);
		}).catch(() => {
			setShowLoader(false);
		});
	}, [scopeFilter]);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		setPage(0);
		getRowsTable(1);
	}, [scopeFilter]);

	const handlePageChange = (newPage) => {
		setPage(newPage);
		getRowsTable(newPage + 1);
	};

	const columns = [
		{ field: 'id', headerName: 'ID', width: 70 },
		{
			field: 'name',
			headerName: 'Назва',
			width: 250,
			renderCell: (params) => (
				<Link
					to={`${urlRouter}/view/${params.row.id}`}
					className="table-name-link"
					title={params.row.name}
				>
					{params.row.name}
				</Link>
			),
		},
		{ field: 'slug', headerName: 'Slug', width: 220 },
		{
			field: 'scopes',
			headerName: 'Область застосування категорій',
			width: 250,
			renderCell: (params) => {
				const parts = (params.row.scopes || []).map(s => {
					const active = scopeOptions.find(o => o.code === s);
					if (active) return { name: active.name, inactive: false };
					const inactive = inactiveScopeOptions.find(o => o.code === s);
					return { name: inactive?.name || s, inactive: true };
				});
				if (!parts.length) return <span>—</span>;
				return (
					<span title={parts.map(p => p.name).join(', ')}>
						{parts.map((p, i) => (
							<React.Fragment key={i}>
								{i > 0 && ', '}
								{p.inactive
									? <span style={{ textDecoration: 'line-through', color: '#999' }}>{p.name}</span>
									: p.name
								}
							</React.Fragment>
						))}
					</span>
				);
			},
		},
		{ field: 'sort_order', headerName: 'Порядок', width: 75 },
		{
			field: 'is_active',
			headerName: 'Активна',
			width: 80,
			renderCell: (params) => (
				<CBadge color={params.row.is_active ? 'success' : 'secondary'}>
					{params.row.is_active ? 'Активна' : 'Неактивна'}
				</CBadge>
			),
		},
		{
			field: 'action',
			headerName: 'Переглянути',
			headerAlign: 'center',
			align: 'center',
			sortable: false,
			width: 105,
			renderCell: (params) => (
				<Link to={`${urlRouter}/view/${params.row.id}`} className='custom-view-icon' title="Переглянути запис">
					<CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
		{
			field: 'action_edit',
			headerName: 'Редагувати',
			headerAlign: 'center',
			align: 'center',
			sortable: false,
			width: 95,
			renderCell: (params) => (
				<Link to={`${urlRouter}/update/${params.row.id}`} className='custom-edit-icon' title="Редагувати запис">
					<CIcon icon={cilPencil} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
	];

	return (
		<CRow>
			<CCol xs={12}>
				<CCard className="mb-4">
					<CAccordion flush>
						<CAccordionItem>
							<CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
							<CAccordionBody>
								<CRow className="my-3 mx-2">
									<CCol md={4} className="filter-field">
										<CFormSelect
											value={scopeFilter}
											onChange={(e) => setScopeFilter(e.target.value)}
										>
											<option value="">Всі scope</option>
											{scopeOptions.map(o => (
												<option key={o.code} value={o.code}>{o.name}</option>
											))}
										</CFormSelect>
									</CCol>
									<CCol md={3} className="ms-auto" style={{ textAlign: 'end' }}>
										<CButton
											color="secondary"
											variant="outline"
											onClick={() => setScopeFilter('')}
										>
											Очистити фільтри
										</CButton>
									</CCol>
								</CRow>
							</CAccordionBody>
						</CAccordionItem>
					</CAccordion>

					<CCardHeader>
						<strong>Категорії підтримки</strong>
					</CCardHeader>
					<CCardBody>
						<p className="text-medium-emphasis small">
							<Link to={`${urlRouter}/create`}>
								<CButton color="secondary" title="Створити нову категорію">
									<CIcon icon={cilPlus} customClassName="nav-icon" height={15} />
									Створити
								</CButton>
							</Link>
						</p>
						<div style={{ height: 600, width: '100%' }}>
							<DataGrid
								rows={rows}
								columns={columns}
								page={page}
								pageSize={itemsPerPage}
								rowsPerPageOptions={[itemsPerPage]}
								pagination
								paginationMode="server"
								rowCount={rowCountState}
								loading={showLoader}
								onPageChange={handlePageChange}
								initialState={{
									sorting: { sortModel: [{ field: 'id', sort: 'asc' }] },
								}}
							/>
						</div>
						<div className="d-flex justify-content-start mt-3">
							<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
								Повернутись
							</CButton>
						</div>
					</CCardBody>
				</CCard>
			</CCol>
		</CRow>
	);
};

export default SupportCategories;
